// Curated real (non-AI) photo sources per service category, with a production-safe
// fallback chain. The <RealPhoto/> component walks the chain on error so the UI
// never shows a broken or obviously-AI image when one source fails.
//
// Unknown slugs return an empty source list — the component then renders the
// bundled local `fallback` immediately.

export type RealPhotoCategory = string;

type Entry = { sources: string[]; alt: string };

const u = (id: string) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=900&q=80`;

const HANDYMAN_FALLBACKS = [u("photo-1581094794329-c8112a89af12"), u("photo-1572297870735-d35e35f96eaf")];
const MOVING_FALLBACKS = [u("photo-1600585154340-be6161a56a0c"), u("photo-1558618666-fcd25c85cd64")];
const ASSEMBLY_FALLBACKS = [u("photo-1581578731548-c64695cc6952"), u("photo-1556909114-f6e7ad7d3136")];
const TV_FALLBACKS = [u("photo-1593784991095-a205069470b6"), u("photo-1593359677879-a4bb92f829d1")];

const PHOTOS: Record<string, Entry> = {
  "handyman": { sources: HANDYMAN_FALLBACKS, alt: "Handyman fixing a fitting in a London home" },
  "tv-mounting": { sources: TV_FALLBACKS, alt: "Helper mounting a flat-screen TV on a living-room wall" },
  "furniture-assembly": { sources: ASSEMBLY_FALLBACKS, alt: "Helper assembling flat-pack furniture in a London flat" },
  "moving": { sources: MOVING_FALLBACKS, alt: "Movers carrying furniture from a Victorian London townhouse" },
  "ikea-assembly": { sources: ASSEMBLY_FALLBACKS, alt: "IKEA flat-pack assembly in a London home" },
  "furniture-disassembly": { sources: ASSEMBLY_FALLBACKS, alt: "Helper disassembling a wardrobe ahead of a move" },
  "furniture-removal": { sources: MOVING_FALLBACKS, alt: "Helper removing old furniture from a London flat" },
  "heavy-lifting": { sources: MOVING_FALLBACKS, alt: "Two helpers carrying a heavy sofa up stairs" },
  "shelf-mounting": { sources: HANDYMAN_FALLBACKS, alt: "Helper mounting wall shelves" },
  "picture-hanging": { sources: HANDYMAN_FALLBACKS, alt: "Helper hanging framed art on a living-room wall" },
  "curtain-blind-installation": { sources: HANDYMAN_FALLBACKS, alt: "Helper installing a window blind" },
  "home-repairs": { sources: HANDYMAN_FALLBACKS, alt: "Handyman doing small home repairs" },
  "appliance-installation": { sources: HANDYMAN_FALLBACKS, alt: "Helper installing a kitchen appliance" },
  "door-repair": { sources: HANDYMAN_FALLBACKS, alt: "Helper adjusting an internal door" },
  "drywall-repair": { sources: HANDYMAN_FALLBACKS, alt: "Helper patching plasterboard" },
};

export const REAL_PHOTOS: Record<string, Entry> = new Proxy(PHOTOS, {
  get(target, prop: string) {
    return target[prop] ?? { sources: [], alt: "Vetted local Helper at work in Greater London" };
  },
});
